import React from 'react';
import { Container, Box, Typography, Chip, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import SEO from '../components/common/SEO';

const HeroSection = styled(Box)({
  background: 'linear-gradient(135deg, #1e3a8a 0%, #7c3aed 100%)',
  color: 'white',
  padding: '64px 0',
  textAlign: 'center',
});

const Section = ({ title, children, id }) => (
  <Box id={id} sx={{ mb: 4, scrollMarginTop: '80px' }}>
    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
    {children}
  </Box>
);

const P = ({ children }) => (
  <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>{children}</Typography>
);

function PrivacyPolicyPage() {
  return (
    <Box>
      <SEO title="Privacy Policy" description="Cyber Wise Corp privacy policy. How we collect, use, and protect your information." />
      <HeroSection>
        <Container maxWidth="lg">
          <Chip label="Legal" sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }} />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Privacy Policy
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Last updated: April 2026
          </Typography>
        </Container>
      </HeroSection>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <P>
          Cyber Wise Corp ("CWC," "we," "us," or "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website at cyberwisecorp.com, our services, or interact with us in any way.
        </P>

        <Divider sx={{ my: 4 }} />

        <Section title="1. Information We Collect">
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Information You Provide</Typography>
          <P>When you use our services, we may collect information you voluntarily provide, including:</P>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: 'text.secondary' } }}>
            <li>Name, email address, phone number, and mailing address (when booking an assessment or registering as an intern)</li>
            <li>Business name and address (for business assessments)</li>
            <li>Academic information such as college, GPA, and year (for intern applicants)</li>
            <li>Payment information (processed securely through Stripe; we never store your credit card details on our servers)</li>
            <li>Messages you send through our contact form</li>
            <li>Video introductions and certification documents (for intern applicants)</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Information Collected During Assessments</Typography>
          <P>
            During a cybersecurity assessment, our interns evaluate your network configuration, device security settings, software update status, and security practices. Our interns do NOT access, view, copy, or record any personal files, emails, photos, financial records, browsing history, or other private content. The assessment evaluates your security posture only.
          </P>
          <P>Assessment data collected includes:</P>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: 'text.secondary' } }}>
            <li>Network configuration details (router settings, encryption type, connected devices)</li>
            <li>Device security status (antivirus presence, update levels, firewall settings)</li>
            <li>Security practice observations (password manager usage, MFA status)</li>
            <li>IoT device inventory and firmware status</li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Automatically Collected Information</Typography>
          <P>
            When you visit our website, we may automatically collect certain information including your IP address, browser type, operating system, referring URLs, and pages viewed. This information is used for analytics purposes to improve our website.
          </P>
        </Section>

        <Section title="2. How We Use Your Information">
          <P>We use the information we collect to:</P>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: 'text.secondary' } }}>
            <li>Provide, maintain, and improve our assessment services</li>
            <li>Process bookings, payments, and refunds</li>
            <li>Match clients with available interns</li>
            <li>Generate and deliver your security assessment report</li>
            <li>Facilitate expert review of assessment findings</li>
            <li>Process intern applications and manage the intern program</li>
            <li>Respond to your inquiries and contact form submissions</li>
            <li>Send booking confirmations, assessment reminders, and report delivery notifications</li>
            <li>Improve our assessment framework and intern training</li>
          </Box>
        </Section>

        <Section id="sms" title="3. SMS Data and Mobile Phone Numbers">
          <P>
            <strong>Mobile phone numbers are collected at the time of booking, intern application, or admin provisioning</strong>, and only when explicitly consented to via an opt-in checkbox on the relevant signup form. Consent is recorded with a timestamp and the exact disclosure text shown at the time of opt-in.
          </P>
          <P>
            <strong>What we do with your phone number:</strong>
          </P>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: 'text.secondary' } }}>
            <li>Send transactional SMS related to your account: booking confirmations, reminders (typically 24h and 1h before), schedule changes, urgent operational alerts, and (for interns) job-availability notifications.</li>
            <li>Allow the assigned intern to call you with directions or arrival questions on the day of the assessment.</li>
            <li>Allow CWC support to contact you about service issues.</li>
          </Box>
          <P>
            <strong>What we do NOT do:</strong> we do not share, sell, rent, or trade your mobile phone number or SMS opt-in status with any third parties or affiliates for marketing purposes. SMS consent is independent of any other marketing — opting out of SMS does not affect your ability to use the service or receive email confirmations.
          </P>
          <P>
            <strong>Opt-out:</strong> reply <strong>STOP</strong> to any SMS we send. You will receive a single confirmation and no further messages. Reply <strong>HELP</strong> for help, or contact us through our contact form.
          </P>
          <P>
            See the <a href="/terms#sms" style={{ color: '#7c3aed' }}>SMS Communications &amp; Consent</a> section of our Terms of Service for the full program details, message types, and frequency.
          </P>
        </Section>

        <Section title="4. How We Share Your Information">
          <P>We do not sell, rent, or trade your personal information to third parties. We may share your information in the following limited circumstances:</P>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: 'text.secondary' } }}>
            <li><strong>With your assigned intern:</strong> Your name, address, phone number, and assessment time are shared with the intern assigned to your booking so they can perform the assessment.</li>
            <li><strong>With our expert reviewers:</strong> Assessment data (not your personal contact information) is shared with the CISSP-certified expert who reviews the findings.</li>
            <li><strong>With service providers:</strong> We use Stripe for payment processing, Amazon Web Services for hosting, and AWS Cognito for authentication. These providers have their own privacy policies.</li>
            <li><strong>As required by law:</strong> We may disclose information if required by law, court order, or government request.</li>
          </Box>
        </Section>

        <Section title="5. Data Security">
          <P>
            We take reasonable measures to protect your information from unauthorized access, alteration, disclosure, or destruction. All data is encrypted in transit using TLS/SSL and at rest using AES-256 encryption on AWS. Access to personal data is restricted to authorized personnel on a need-to-know basis.
          </P>
          <P>
            Assessment data collected during your evaluation is stored securely and access is limited to your assigned intern and reviewing expert. Interns use dedicated assessment devices and all connection data is purged after each assessment visit.
          </P>
        </Section>

        <Section title="6. Data Retention">
          <P>
            We retain your personal information for as long as necessary to provide our services and fulfill the purposes described in this policy. Assessment reports are retained for 2 years to allow you to request copies. Intern application materials are retained for the duration of the intern's engagement plus 1 year. You may request deletion of your data at any time by contacting us through our contact form.
          </P>
        </Section>

        <Section title="7. Your Rights">
          <P>Depending on your location, you may have the right to:</P>
          <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1, color: 'text.secondary' } }}>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your personal information</li>
            <li>Opt out of marketing communications</li>
            <li>Request a copy of your data in a portable format</li>
          </Box>
          <P>To exercise any of these rights, please contact us through the contact form on our website.</P>
        </Section>

        <Section title="8. Cookies and Tracking">
          <P>
            Our website may use cookies and similar tracking technologies to improve your browsing experience and analyze website traffic. You can control cookie preferences through your browser settings. We do not use cookies for advertising or sell cookie data to third parties.
          </P>
        </Section>

        <Section title="9. Children's Privacy">
          <P>
            Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children under 18. If you believe we have collected information from a minor, please contact us immediately and we will delete that information.
          </P>
        </Section>

        <Section title="10. Changes to This Policy">
          <P>
            We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date at the top of this page. We encourage you to review this policy periodically for any changes. Your continued use of our services after changes are posted constitutes your acceptance of the revised policy.
          </P>
        </Section>

        <Section title="11. Contact Us">
          <P>
            If you have questions about this Privacy Policy or our data practices, please contact us through the contact form on our website. We are based in Glendale, Arizona.
          </P>
        </Section>
      </Container>
    </Box>
  );
}

export default PrivacyPolicyPage;
